import { Link, useLocation } from "wouter";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { CONTACT, IMAGES } from "@/lib/constants";
import AwardSeal from "./AwardSeal";

export default function Footer() {
  const [, setLocation] = useLocation();

  // Navigate without scrolling to top (user preference for footer links)
  const handleFooterNav = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    setLocation(href);
  };

  return (
    <footer className="bg-gray-900 text-white" role="contentinfo">
      {/* CTA Band */}
      <div className="bg-[#2563EB]">
        <div className="container py-10 md:py-14 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-white">
              Vill du veta mer om våra tjänster?
            </h2>
            <p className="text-white mt-1">
              Oavsett om du behöver enstaka detaljer eller stora volymer skär vi snabbt, exakt och 
              anpassat efter era behov. Kontakta oss idag.
            </p>
          </div>
          <Link
            href="/kontakt"
            className="inline-flex items-center px-8 py-4 bg-white text-[#2563EB] font-semibold rounded-md hover:bg-gray-100 transition-colors whitespace-nowrap"
          >
            Begär offert
          </Link>
        </div>
      </div>

      {/* Main Footer */}
      <div className="container py-14 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-8">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="mb-4">
              <img
                src={IMAGES.logo}
                alt="ASPAB logotyp"
                className="h-9 w-auto"
                width={135}
                height={36}
              />
            </div>
            <p className="text-white/70 text-sm leading-relaxed max-w-sm">
              ASPAB &ndash; din specialist inom högkvalitativ laserskärning, kantpressning och svetsning. 
              Med modern teknik och gedigen erfarenhet sedan 1993 erbjuder vi precision och kvalitet 
              i varje projekt.
            </p>
          </div>

          {/* Tjänster */}
          <nav aria-label="Tjänster">
            <h3 className="font-heading text-white font-semibold text-sm tracking-wider uppercase mb-4">Tjänster</h3>
            <ul className="space-y-2.5">
              <li>
                <a href="/tjanster/laserskarning" onClick={(e) => handleFooterNav(e, "/tjanster/laserskarning")} className="text-white/70 text-sm hover:text-white transition-colors">
                  Laserskärning
                </a>
              </li>
              <li>
                <a href="/tjanster/kantpressning" onClick={(e) => handleFooterNav(e, "/tjanster/kantpressning")} className="text-white/70 text-sm hover:text-white transition-colors">
                  Kantpressning
                </a>
              </li>
              <li>
                <a href="/tjanster/svetsning" onClick={(e) => handleFooterNav(e, "/tjanster/svetsning")} className="text-white/70 text-sm hover:text-white transition-colors">
                  Svetsning
                </a>
              </li>
              <li>
                <a href="/maskinpark" onClick={(e) => handleFooterNav(e, "/maskinpark")} className="text-white/70 text-sm hover:text-white transition-colors">
                  Maskinpark
                </a>
              </li>
              <li>
                <a href="/tjanster/laserskarning#leveransomraden" className="text-sm font-medium text-[#60A5FA] transition-colors hover:text-white">
                  Laserskärning nära dig
                </a>
              </li>
            </ul>
          </nav>

          {/* Företaget */}
          <nav aria-label="Företaget">
            <h3 className="font-heading text-white font-semibold text-sm tracking-wider uppercase mb-4">Företaget</h3>
            <ul className="space-y-2.5">
              <li>
                <a href="/om-oss" onClick={(e) => handleFooterNav(e, "/om-oss")} className="text-white/70 text-sm hover:text-white transition-colors">
                  Om oss
                </a>
              </li>
              <li>
                <a href="/referenser" onClick={(e) => handleFooterNav(e, "/referenser")} className="text-white/70 text-sm hover:text-white transition-colors">
                  Referenser
                </a>
              </li>
              <li>
                <a href="/kontakt" onClick={(e) => handleFooterNav(e, "/kontakt")} className="text-white/70 text-sm hover:text-white transition-colors">
                  Kontakt
                </a>
              </li>
              <li>
                <a href="/nyheter" onClick={(e) => handleFooterNav(e, "/nyheter")} className="text-white/70 text-sm hover:text-white transition-colors">
                  Kunskapsbank
                </a>
              </li>
              <li>
                <a href="/forfattare" onClick={(e) => handleFooterNav(e, "/forfattare")} className="text-white/70 text-sm hover:text-white transition-colors">
                  Våra författare
                </a>
              </li>
              <li>
                <a href="/policyer" onClick={(e) => handleFooterNav(e, "/policyer")} className="text-white/70 text-sm hover:text-white transition-colors">
                  Policyer och villkor
                </a>
              </li>
            </ul>
          </nav>

          {/* Kontakt */}
          <div>
            <h3 className="font-heading text-white font-semibold text-sm tracking-wider uppercase mb-4">Kontakt</h3>
            <ul className="space-y-3">
              <li>
                <a href={`tel:${CONTACT.phone.replace(/[^0-9+]/g, "")}`} className="flex items-center gap-2.5 text-white/70 text-sm hover:text-white transition-colors">
                  <Phone className="w-4 h-4 text-[#60A5FA] shrink-0" />
                  {CONTACT.phone}
                </a>
              </li>
              <li>
                <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-2.5 text-white/70 text-sm hover:text-white transition-colors">
                  <Mail className="w-4 h-4 text-[#60A5FA] shrink-0" />
                  {CONTACT.email}
                </a>
              </li>
              <li>
                <div className="flex items-start gap-2.5 text-white/70 text-sm">
                  <MapPin className="w-4 h-4 text-[#60A5FA] shrink-0 mt-0.5" />
                  <span>{CONTACT.address}<br />{CONTACT.city}</span>
                </div>
              </li>
              <li>
                <div className="flex items-start gap-2.5 text-white/70 text-sm">
                  <Clock className="w-4 h-4 text-[#60A5FA] shrink-0 mt-0.5" />
                  <span>Mån&ndash;Fre: 07:00&ndash;16:00</span>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Trust marks + Bottom Bar */}
        <div className="mt-14 pt-8 border-t border-white/10">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-5 xl:hidden">
                <img
                  src={IMAGES.kreditvardighet}
                  alt="Högsta kreditvärdighet AAA – Dun & Bradstreet"
                  className="h-14 w-auto"
                  width={120}
                  height={56}
                />
                <AwardSeal
                  variant="dark"
                  placement="above"
                  tooltipId="branschvinnare-tooltip-footer"
                  imageClassName="h-16 w-auto"
                  width={150}
                  height={64}
                />
              </div>
              <p className="text-white/50 text-xs text-center sm:text-left">
                &copy; {new Date().getFullYear()} ASPAB. Alla rättigheter förbehållna.<br />
                Org.nr: {CONTACT.orgNr}
              </p>
            </div>
            <div className="flex flex-wrap gap-4 justify-center lg:justify-end">
              <a href="/policyer" onClick={(e) => handleFooterNav(e, "/policyer")} className="text-white/50 text-xs hover:text-white/70 transition-colors">
                Policyer och villkor
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
